import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Card } from "@/components/ui/card";
import { TimelineEvent as TimelineEventType } from "@/types/vehicleCard";
import { getEventIcon } from "./utils/eventIcons";
import { translateEventDetails } from "@/i18n/utils";
import { ListingAuctionStatsButton } from "@/components/listing/ListingAuctionStatsButton";
import type { ListingStatus } from "@/types/listing";
import { parseListingTitleFromEventDetails } from "@/utils/resolveListingFromEvent";

interface TimelineEventProps {
  event: TimelineEventType;
  vehicleName?: string;
  regNumber?: string;
}

const LISTING_ID_PATTERN = /listingId:([a-f0-9-]+)/i;

function extractListingId(details?: string): string | null {
  if (!details) return null;
  const match = details.match(LISTING_ID_PATTERN);
  return match?.[1] ?? null;
}

function stripListingIdFromDetails(details: string): string {
  return details
    .replace(/\.\s*listingId:[a-f0-9-]+/gi, "")
    .replace(/listingId:[a-f0-9-]+/gi, "")
    .trim();
}

function isAuctionListingEvent(details?: string): boolean {
  const detailsLower = (details || "").toLowerCase();
  return (
    detailsLower.includes("typ: auktion") ||
    detailsLower.includes("type: auction")
  );
}

function shouldShowAuctionStats(description: string, details?: string): boolean {
  const normalized = description.toLowerCase();
  if (normalized.includes("annons avslutad - auktion")) return true;
  if (
    (normalized.includes("annons avbruten") ||
      normalized.includes("listing cancelled")) &&
    isAuctionListingEvent(details)
  ) {
    return true;
  }
  if (normalized.includes("annons såld") && isAuctionListingEvent(details)) {
    return true;
  }
  return false;
}

function getAuctionStatsStatus(description: string): ListingStatus {
  const normalized = description.toLowerCase();
  if (
    normalized.includes("avbruten") ||
    normalized.includes("cancelled")
  ) {
    return "cancelled";
  }
  return "completed";
}

export const TimelineEventCard = ({
  event,
  vehicleName = "",
  regNumber,
}: TimelineEventProps) => {
  const isCustomerAction = event.type === "message" || event.type === "view";
  const iconPositionClass = isCustomerAction ? "-left-[52px]" : "-right-[52px]";
  const listingId = extractListingId(event.details);
  const showStatsButton = shouldShowAuctionStats(
    event.description,
    event.details,
  );
  const statsStatus = getAuctionStatsStatus(event.description);
  const displayDetails = event.details
    ? stripListingIdFromDetails(translateEventDetails(event.details))
    : "";

  const formattedTimestamp = event.timestamp
    ? format(new Date(event.timestamp), "d MMM yyyy HH:mm", { locale: sv })
    : "";

  return (
    <Card
      className={`p-4 ${
        isCustomerAction ? "ml-12" : "mr-12"
      } relative hover:shadow-md transition-shadow duration-200 bg-background`}
    >
      <div
        className={`absolute ${iconPositionClass} w-8 h-8 rounded-full bg-card border flex items-center justify-center shadow-sm ${
          isCustomerAction ? "bg-blue-50" : "bg-purple-50"
        }`}
      >
        {getEventIcon(event.type)}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <p className="font-medium text-foreground">{event.description}</p>
            <p className="text-sm text-muted-foreground">
              {event.user || "System"} • {formattedTimestamp}
            </p>
          </div>
          {showStatsButton && (
            <ListingAuctionStatsButton
              listingId={listingId}
              fallbackResolve={{
                regNumber,
                title: parseListingTitleFromEventDetails(event.details),
              }}
              vehicleName={vehicleName}
              regNumber={regNumber}
              listingStatus={statsStatus}
              className="flex-shrink-0"
            />
          )}
        </div>

        {displayDetails && (
          <p className="text-sm text-muted-foreground mt-2 bg-muted/50 p-3 rounded-md">
            {displayDetails}
          </p>
        )}
      </div>
    </Card>
  );
};
